import { useState } from "react";
import { Link } from "react-router-dom";
import { PaymentIcons } from "@/features/checkout/PaymentIcons";
import { KiyohBadge } from "@/components/KiyohBadge";
import { COMPANY_PROFILES } from "@/lib/company";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useT } from "@/lib/i18n";

/** Footer-linkkolommen — alleen bestaande pagina's/categorieën. */
const COLUMNS: { title: string; links: { label: string; to: string }[] }[] = [
  {
    // "Glassoorten" en niet "Glas op maat": die kop stond óók al als uitgelicht-kaart op de
    // homepage, en twee identieke koppen op één pagina leest voor Google (en voor een
    // schermlezer die door de koppen springt) als dezelfde sectie. Dekt de lading beter ook:
    // hieronder staan de soorten, niet de configurator.
    title: "Glassoorten",
    links: [
      { label: "Enkel glas", to: "/glas-op-maat" },
      { label: "Isolatieglas", to: "/categorie/isolatieglas" },
      { label: "Vacuümglas", to: "/vacuumglas" },
      { label: "Spiegels op maat", to: "/glas-op-maat/spiegels-op-maat" },
      { label: "Alle glassoorten", to: "/glassoorten" },
      { label: "Glas laten plaatsen", to: "/glas-laten-plaatsen" },
    ],
  },
  {
    title: "Beslag & artikelen",
    links: [
      { label: "Douche & bad", to: "/categorie/douche-en-bad" },
      { label: "Interieurbeglazing", to: "/categorie/interieurbeglazing" },
      { label: "Vitrine- & meubelbeslag", to: "/categorie/vitrine-en-meubelbeslag" },
      { label: "Balustrade", to: "/categorie/balustrade" },
      { label: "Glasartikelen", to: "/categorie/glasartikelen" },
      { label: "Ventilatieroosters", to: "/categorie/ventilatieroosters" },
    ],
  },
  {
    title: "Klantenservice",
    links: [
      { label: "Levertijden & verzending", to: "/levertijden" },
      { label: "Bezorging in uw stad", to: "/glas-bezorgen" },
      { label: "Herroepingsrecht", to: "/herroepingsrecht" },
      { label: "Garantie & transportschade", to: "/garantie" },
      { label: "Bestelling herroepen", to: "/herroepen" },
      { label: "Retour aanmelden", to: "/retour" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Over Glastotaal",
    links: [
      { label: "Zakelijk bestellen", to: "/zakelijk" },
      { label: "Over ons", to: "/over-ons" },
      { label: "Reviews", to: "/reviews" },
      { label: "Kennisbank", to: "/kennisbank" },
      { label: "Veelgestelde vragen", to: "/veelgestelde-vragen" },
      { label: "Glas samenstellen", to: "/glassoorten" },
    ],
  },
];

type Status = "idle" | "loading" | "done" | "already" | "error";

function NewsletterSignup() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      // firebase/functions pas hier laden (dynamische import): de footer staat op élke pagina,
      // dus zo blijft de functions-SDK uit de eager bundle en laadt 'ie alleen bij inschrijven.
      const [{ httpsCallable }, { getFns }] = await Promise.all([
        import("firebase/functions"),
        import("@/lib/firebase/functions"),
      ]);
      const res = await httpsCallable<{ email: string }, { ok: true; already: boolean }>(
        getFns(),
        "subscribeNewsletter",
      )({ email });
      // GA4-conversie "sign_up" — alleen bij een écht nieuwe inschrijving, niet bij "was al
      // ingeschreven". Nieuwsbrief-inschrijvingen stonden nergens in de meting: ze gingen alleen
      // naar Firestore, geen enkel GA4-event markeerde het moment.
      if (!res.data.already) {
        const { trackNewsletterSignup } = await import("@/lib/track");
        trackNewsletterSignup();
      }
      setStatus(res.data.already ? "already" : "done");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  const done = status === "done" || status === "already";

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-12">
      <div className="md:max-w-md">
        <h2 className="text-lg font-bold sm:text-xl">{t("Meld u aan voor onze nieuwsbrief")}</h2>
        <p className="mt-1 text-sm text-white/80 sm:text-base">
          {t("Ontvang de beste aanbiedingen en glas-op-maat-tips. Geen spam.")}{" "}
          {/* Uitschrijven moet net zo makkelijk zijn als inschrijven. */}
          <Link to="/nieuwsbrief/afmelden" className="underline decoration-white/40 underline-offset-2 hover:decoration-white">
            {t("Uitschrijven")}
          </Link>
        </p>
      </div>
      <div className="w-full md:w-[480px] md:shrink-0">
        {done ? (
          <p className="bg-white/15 px-4 py-3 text-sm font-medium">
            {status === "already" ? t("U bent al ingeschreven — bedankt!") : t("Gelukt! U ontvangt voortaan onze nieuwsbrief.")}
          </p>
        ) : (
          <form onSubmit={submit}>
            {/* Mobiel: veld + pijl-knop náást elkaar; desktop: knop met tekst. */}
            <div className="flex gap-2">
              <input
                id="nl-email"
                type="email"
                required
                aria-label={t("E-mailadres")}
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                placeholder={t("Wat is uw e-mail?")}
                className="min-h-11 flex-1 border border-transparent bg-white px-4 text-base text-primary-900 outline-none placeholder:text-primary-600 focus-visible:ring-2 focus-visible:ring-white"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                aria-label={t("Inschrijven")}
                className="flex min-h-11 w-12 shrink-0 items-center justify-center bg-accent-700 font-semibold text-white transition hover:bg-accent-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-70 sm:w-auto sm:px-6"
              >
                <span className="hidden sm:inline">{status === "loading" ? t("Bezig…") : t("Inschrijven")}</span>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="sm:hidden">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
            {status === "error" && (
              <p className="mt-2 text-sm text-white">{t("Er ging iets mis. Controleer uw e-mailadres en probeer het opnieuw.")}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

/** Blauwe footer (Coolblue-stijl): servicebalk + nieuwsbrief, linkkolommen, betaalbalk. */

/**
 * De rij social-iconen onder het logo is er per 10-08-2026 uit.
 *
 * - Het sterretje ging naar /reviews en de envelop naar ons e-mailadres; allebei staan die al als
 *   gewone link in de kolommen hiernaast ("Reviews", "Contact"). Een icoon dat hetzelfde doet als
 *   een link twee centimeter verderop voegt niets toe, en de plek is nu voor het Kiyoh-keurmerk.
 * - LinkedIn is verhuisd naar de juridische balk onderaan, als woord. Zie LINKEDIN_URL daar.
 *
 * Géén Facebook/Instagram: daar is Glastotaal niet actief, en een link die nergens heen gaat wekt
 * de indruk dat er wél een profiel is.
 */

export function SiteFooter() {
  const t = useT();
  // Hier stond de Google Klantenreviews-badge (merchantwidget, Merchant Center 5832488495). Weg
  // per 10-08-2026: het reviewen loopt volledig via Kiyoh, en Google's eigen programma vroeg de
  // klant bij het afrekenen óók om een beoordeling — twee verzoeken voor dezelfde bestelling.
  // Terugzetten kan, maar dan wel weer achter marketing-toestemming én alleen op desktop: het
  // badge-iframe negeert de gevraagde positie, gaat rechtsonder zitten met een onzichtbaar veel te
  // groot klikvlak, en ving zo de kliks op de mobiele "In winkelwagen"-knop af.
  return (
    <footer className="bg-[#013878] text-white">
      {/* Nieuwsbrief — volle band bovenin (kop links, formulier rechts) */}
      <div className="border-b border-white/15">
        <div className="mx-auto max-w-[1600px] px-4 py-6 sm:py-8">
          <NewsletterSignup />
        </div>
      </div>

      {/* Merk + link-kolommen — merk breder links, 4 gelijke kolommen over de rest */}
      <div className="mx-auto grid max-w-[1600px] gap-8 px-4 py-12 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr] lg:gap-10">
        <div className="sm:col-span-2 lg:col-span-1">
          <Link to="/" aria-label={t("Naar de homepagina")} className="inline-block text-2xl font-extrabold tracking-tight transition hover:opacity-90">
            GLASTOTAAL<span className="text-accent-500">.NL</span>
          </Link>
          {/*
            Kiyoh-keurmerk, in de smalle staande variant (120×160) met Kiyoh's donkere thema.

            Waarom `thema="donker"`: hun stylesheet zet dan de tekst op wit én laat de achtergrond
            doorzichtig (.kv-widget-body.theme-dark + .transparent{background:0 0}, geverifieerd
            10-08-2026). Daarmee vervalt het witte kader dat hier eerst omheen zat — het plaatje
            staat nu gewoon op onze navy. Alleen de waarde "dark" schakelt dat om; "black", "grey"
            en de rest vallen stil terug op het lichte thema.

            De beschrijvende regel die hier stond ("Glas op maat & glasartikelen — verzekerd
            geleverd…") is per 10-08-2026 weg om ruimte te maken; diezelfde belofte staat al in de
            USP-balk direct boven de footer.
          */}
          <div className="mt-5">
            <KiyohBadge size="md" thema="donker" />
          </div>

        </div>

        {/* Mobiel: kolommen als accordions (bol.com-stijl); vanaf sm open kolommen */}
        {COLUMNS.map((col) => (
          <nav key={col.title} aria-label={t(col.title)} className="hidden text-sm sm:block">
            <h3 className="text-base font-bold">{t(col.title)}</h3>
            <ul className="mt-3 space-y-2">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-white/80 transition hover:text-white">{t(l.label)}</Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
        <div className="-mx-1 divide-y divide-white/15 border-y border-white/15 sm:hidden">
          {COLUMNS.map((col) => (
            <details key={col.title} className="group px-1">
              <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between text-base font-bold">
                {t(col.title)}
                <span aria-hidden className="text-white/60 transition group-open:rotate-180">▾</span>
              </summary>
              <ul className="space-y-2.5 pb-4 text-sm">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-white/80 transition hover:text-white">{t(l.label)}</Link>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </div>

      {/* Betaalbalk */}
      <div className="border-t border-white/15">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-3 px-4 py-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-white/85">{t("Veilig betalen met")}</p>
          <PaymentIcons />
        </div>
      </div>
    </footer>
  );
}

/** Juridische links + taal + Kiyoh-reviews. Staat onder de footer en de witte USP-balk. */
const LEGAL_LINKS: { label: string; to: string }[] = [
  { label: "Algemene voorwaarden", to: "/algemene-voorwaarden" },
  { label: "Overeenkomst ontbinden", to: "/herroepen" },
  { label: "Privacy", to: "/privacy" },
  { label: "Cookies", to: "/cookies" },
];

/** LinkedIn-bedrijfspagina. Komt uit COMPANY_PROFILES (packages/core), dezelfde lijst die als
 *  `sameAs` in de structuurdata staat — één bron, zodat site en Google nooit uit de pas lopen.
 *  Stond eerst als icoon onder het logo; sinds 10-08-2026 als woord achter de juridische links. */
const LINKEDIN_URL = COMPANY_PROFILES.find((u) => u.includes("linkedin.com")) ?? "";

export function FooterLegalBar() {
  const t = useT();
  return (
    <div className="border-t border-primary-100 bg-white">
      <div className="mx-auto flex max-w-[1600px] flex-col items-center gap-2.5 px-4 py-4 text-sm md:flex-row md:justify-between md:gap-4">
        {/* Rij 1 (mobiel): juridische links, rustig gecentreerd in klein grijs */}
        <nav aria-label="Juridisch en taal" className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-primary-600 md:gap-x-5 md:text-sm">
          {LEGAL_LINKS.map((l) => (
            <Link key={l.label} to={l.to} className="transition hover:text-primary-900">
              {t(l.label)}
            </Link>
          ))}
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="transition hover:text-primary-900">
            LinkedIn
          </a>
        </nav>
        {/* Sterren + cijfer · taal · copyright. Dit is onze eigen compacte weergave (zelfde bron als
            het Kiyoh-plaatje in de navy footer erboven, maar in huisstijl en zonder extra iframe —
            deze balk staat op élke pagina). */}
        <div className="flex items-center gap-3 md:gap-4">
          <span className="hidden md:block"><KiyohBadge size="sm" /></span>
          <LanguageSwitcher tone="footer" />
          <span className="text-xs text-primary-600">© Glastotaal</span>
        </div>
      </div>
      {/* Mobiel: de sterren op een eigen gecentreerde rij — naast taal + copyright is het te krap. */}
      <div className="mx-auto flex max-w-[1600px] justify-center border-t border-primary-50 px-4 py-4 md:hidden">
        <KiyohBadge size="sm" />
      </div>
    </div>
  );
}
